// CombinedSavingsTile — Dashboard v3 (Bram cycle #21, redesign cycle #34).
//
// Double-width tegel (2 kolommen in 3×5 grid). Layout:
//   Links:  Hoofdwaarde (totaal vandaag, groot, groen) + cumulatief totaal
//           met zijn periode ("over N dagen", wachtrij #133).
//   Rechts: Vandaag-uitsplitsing in 3 labeled rijen:
//             • PV eigenverbruik  — vermeden import
//             • PV export         — opbrengst teruglevering
//             • Batterij arbitrage — kopen goedkoop, verkopen duur
//
// Tooltip toont volledige breakdown inclusief cumulatieve bedragen.

import './kpi.css';
import type { CSSProperties } from 'react';
import { fmtEur } from '../../lib/format';
import type { SavingsTotal } from '../../api/savings';

interface Props {
  /** /api/savings/total response (cumulatief + today breakdown). */
  savingsTotal: SavingsTotal | null;
  /**
   * Fallback today-totaal (uit computeSavings(history.samples)) wanneer
   * server nog geen extended `today` block heeft. Pre-cycle #11 Pi's.
   */
  fallbackTodayEur: number | null;
  /**
   * Cycle #50: fallback breakdown (PV selfuse / PV export / arbitrage) uit
   * client-side computeSavings(history) zodat de drie rijen ook tonen als
   * /api/savings/total timeout't (504 op trage Pi's). Volgorde matcht
   * SavingsTodayBreakdown server-side velden.
   */
  fallbackBreakdown?: {
    pvSelfUseEur: number;
    pvExportEur: number;
    arbitrageEur: number;
  } | null;
}

export function CombinedSavingsTile({ savingsTotal, fallbackTodayEur, fallbackBreakdown }: Props) {
  // Cycle #399 W1 (live VJ 2026-07-28, state.db page-corruptie): server geeft
  // 200 + degraded:true als de savings-query op beschadigde pages stuit.
  //
  // Twee smaken, bewust verschillend gerenderd:
  //   stale=true  → laatst-bekende-goede bedragen. Blijven ZICHTBAAR (nog
  //                 steeds bruikbaar) maar met leeftijds-markering.
  //   stale=false → geen last-known-good; server stuurde nullen. Die nul is
  //                 een artefact van de corruptie, GEEN meting. Hem als
  //                 "€ 0,00" tonen is een verzonnen getal met de autoriteit
  //                 van een meting — de operator kan er niet aan zien dat het
  //                 kapot is. Daarom: em-dash + expliciete melding.
  const isDegraded = savingsTotal?.degraded === true;
  const isStale = isDegraded && savingsTotal?.stale === true;
  const isUnavailable = isDegraded && !isStale;

  const staleSince = savingsTotal?.stale_since_iso
    ? new Date(savingsTotal.stale_since_iso)
    : null;
  const staleSinceLabel =
    staleSince && !Number.isNaN(staleSince.getTime())
      ? staleSince.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
      : null;

  // Hoofdwaarde: today.total_eur als server-side breakdown er is,
  // anders fallback. Bij geen van beide → em-dash.
  // Bij isUnavailable onderdrukken we de (verzonnen) nul volledig.
  const todayEur = isUnavailable
    ? null
    : savingsTotal?.today?.total_eur ??
      fallbackTodayEur ??
      null;

  // Cumulatief totaal sinds since_iso. Wachtrij #133: dat is NIET "sinds
  // install" maar de oudste bucket in state.db, en die verschilt per site
  // (2026-09-30: VJ 53, HO 68, NS 149 dagen). Het label noemt daarom de
  // periode; zonder dat vergelijkt een vlootblik 53 met 149 dagen.
  const sinceInstallEur = isUnavailable ? null : savingsTotal?.total_savings_eur ?? null;
  const spanDays = (() => {
    if (savingsTotal?.span_days != null) return savingsTotal.span_days;
    if (!savingsTotal?.since_iso || !savingsTotal?.until_iso) return null;
    const ms =
      new Date(savingsTotal.until_iso).getTime() - new Date(savingsTotal.since_iso).getTime();
    return Number.isFinite(ms) && ms >= 0 ? ms / 86_400_000 : null;
  })();
  const spanDaysRounded = spanDays != null ? Math.round(spanDays) : null;
  const periodLabel =
    spanDaysRounded != null
      ? ` over ${spanDaysRounded} ${spanDaysRounded === 1 ? 'dag' : 'dagen'}`
      : '';
  const coveredDays = savingsTotal?.days_covered ?? null;

  // Today-uitsplitsing: drie afzonderlijke bronnen. Cycle #50: val terug
  // op client-side fallbackBreakdown als de server-side breakdown ontbreekt.
  // Bij isUnavailable: ook de breakdown-nullen onderdrukken. Wél de
  // client-side fallback toestaan — die komt uit history en is een echte
  // meting, dus juist bruikbaar als de server-DB stuk is.
  const todaySelfuseEur = isUnavailable
    ? fallbackBreakdown?.pvSelfUseEur ?? null
    : savingsTotal?.today?.pv_selfuse_eur ?? fallbackBreakdown?.pvSelfUseEur ?? null;
  const todayExportEur = isUnavailable
    ? fallbackBreakdown?.pvExportEur ?? null
    : savingsTotal?.today?.pv_export_eur ?? fallbackBreakdown?.pvExportEur ?? null;
  const todayArbEur = isUnavailable
    ? fallbackBreakdown?.arbitrageEur ?? null
    : savingsTotal?.today?.arbitrage_eur ?? fallbackBreakdown?.arbitrageEur ?? null;
  const hasBreakdown = todaySelfuseEur !== null && todayExportEur !== null && todayArbEur !== null;
  // True wanneer alleen client-side fallback is gebruikt — operator hint via tooltip.
  const breakdownIsFallback =
    hasBreakdown && (savingsTotal?.today?.pv_selfuse_eur ?? null) === null;
  // Wachtrij #182: de terugvalrijen stonden onder exact hetzelfde label en
  // dezelfde typografie als de serverrijen; het enige verschil zat in de
  // tooltip, en die leest niemand bij een storing. Een client-side schatting
  // uit history is een andere meting dan de serverboekhouding (andere
  // last-definitie, geen cycling-cost, geen last-known-good), dus hij krijgt
  // zijn eigen achtervoegsel in de rij zelf.
  const rowSuffix = breakdownIsFallback ? ' (schatting)' : '';

  // Tooltip met volledige breakdown — operator kan hover'en voor detail.
  const tooltipLines: string[] = [];
  if (isDegraded) {
    tooltipLines.push(
      isStale
        ? `Verouderd: database degraded, laatst verse waarde ${staleSinceLabel ?? 'onbekend'}.`
        : 'Savings niet beschikbaar: database degraded (page-corruptie).',
      savingsTotal?.degraded_reason ?? '',
      'EMS-regeling en batterijaansturing zijn hierdoor NIET aangetast.',
      '',
    );
  }
  if (savingsTotal?.today && !isUnavailable) {
    tooltipLines.push(
      `Vandaag: ${fmtEur(savingsTotal.today.total_eur)}`,
      `  PV eigenverbruik: ${fmtEur(savingsTotal.today.pv_selfuse_eur)}`,
      `  PV export: ${fmtEur(savingsTotal.today.pv_export_eur)}`,
      `  Batterij arbitrage: ${fmtEur(savingsTotal.today.arbitrage_eur)}`,
    );
  } else if (breakdownIsFallback && hasBreakdown) {
    tooltipLines.push(
      `Vandaag (client-side schatting uit history):`,
      `  PV eigenverbruik: ${fmtEur(todaySelfuseEur!)}`,
      `  PV export: ${fmtEur(todayExportEur!)}`,
      `  Batterij arbitrage: ${fmtEur(todayArbEur!)}`,
    );
  }
  if (savingsTotal?.breakdown && !isUnavailable) {
    tooltipLines.push(
      '',
      `Totaal${periodLabel}: ${fmtEur(savingsTotal.total_savings_eur)}`,
      `  PV eigenverbruik: ${fmtEur(savingsTotal.breakdown.pv_selfuse_eur)}`,
      `  PV export: ${fmtEur(savingsTotal.breakdown.pv_export_eur)}`,
      `  Batterij arbitrage: ${fmtEur(savingsTotal.breakdown.arbitrage_eur)}`,
    );
  }
  if (savingsTotal?.since_iso) {
    const d = new Date(savingsTotal.since_iso);
    tooltipLines.push('', `Sinds: ${d.toLocaleDateString('nl-NL')} (oudste meting in de database)`);
    // Alleen noemen als de gaten er werkelijk toe doen (>1 dag verschil).
    if (coveredDays != null && spanDays != null && spanDays - coveredDays > 1) {
      tooltipLines.push(`Daarvan ${Math.round(coveredDays)} dagen met data.`);
    }
    tooltipLines.push('Die startdatum verschilt per site: vergelijk sites niet op dit totaal.');
  }
  // Audit #15 W2-Pillar9 / BH-019: dit getal is een spot-calc die GEEN
  // cycling-cost aftrekt. revenue_accounting.compute_hourly_revenue doet
  // dat wél (~€5/dag verschil op zware dagen). Operator hint via tooltip
  // + subtiel label in de UI om verwarring te voorkomen.
  tooltipLines.push('', 'Exclusief cycling-cost (batterij-slijtage).');
  const tooltip = tooltipLines.length > 0 ? tooltipLines.join('\n') : undefined;

  return (
    <div
      className="kpi-tile combined-savings-tile"
      style={{ ['--accent' as string]: '#22c55e' } as CSSProperties}
      title={tooltip}
    >
      {/* Boven (desktop 1-col) / links (mobile 2-col): totaal vandaag + cumulatief */}
      <div className="combined-savings-main">
        <div className="kpi-label">Verdiend</div>
        <div className="kpi-value">
          {todayEur != null ? (
            <>{fmtEur(todayEur)} <span className="unit">vandaag</span></>
          ) : '—'}
        </div>
        {sinceInstallEur != null && (
          <div className="kpi-sub">{`totaal ${fmtEur(sinceInstallEur)}${periodLabel}`}</div>
        )}
        {/* Cycle #399 W1: DB-degradatie moet zichtbaar zijn in de tegel zelf,
            niet alleen in de tooltip. Zonder dit toont de tegel bij corruptie
            een stille "€ 0,00" die van een echte nul-dag niet te
            onderscheiden is. */}
        {isUnavailable && (
          <div className="kpi-sub" style={{ color: '#f59e0b' }}>
            niet beschikbaar — database degraded
          </div>
        )}
        {isStale && (
          <div className="kpi-sub" style={{ color: '#f59e0b' }}>
            {staleSinceLabel
              ? `verouderd (${staleSinceLabel}) — database degraded`
              : 'verouderd — database degraded'}
          </div>
        )}
        {/* Audit #15 W2-Pillar9 / BH-019: dit is spot-calc zonder cycling-cost
            (~€5/dag verschil met revenue_accounting). Subtitel disambigueert. */}
        <div className="kpi-sub" style={{ opacity: 0.7, fontStyle: 'italic' }}>
          (excl. cycling)
        </div>
      </div>

      {/* Onder (desktop) / rechts (mobile 2-col): uitsplitsing vandaag */}
      {hasBreakdown && (
        <div className="combined-savings-breakdown">
          <div className="breakdown-row">
            <span className="breakdown-label">{`PV eigenverbruik${rowSuffix}`}</span>
            <span className="breakdown-value">{fmtEur(todaySelfuseEur!)}</span>
          </div>
          <div className="breakdown-row">
            <span className="breakdown-label">{`PV export${rowSuffix}`}</span>
            <span className="breakdown-value">{fmtEur(todayExportEur!)}</span>
          </div>
          <div className="breakdown-row">
            <span className="breakdown-label">{`Arbitrage${rowSuffix}`}</span>
            <span className="breakdown-value">{fmtEur(todayArbEur!)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
